// Filter chip definitions — ordered by priority for responsive overflow.

export interface SearchFilters {
  from?: string;
  to?: string;
  agentName?: string;
  agentNameExactMatch?: boolean;
  siteId?: string;
  interactionType?: string;
  tags?: string[];
  direction?: string;
  phoneNumber?: string;
  dnis?: string;
  extension?: string;
  device?: string;
  group?: string;
  skill?: string;
  legalHold?: boolean;
  hasMedia?: boolean;
  minDuration?: number;
  maxDuration?: number;
  sourceId?: string;
  interactionId?: string;
  masterInteractionId?: string;
  segmentId?: string;
  callId?: string;
  hasTranscription?: boolean;
}

export type FilterPopoverType =
  | "date"
  | "typeahead"
  | "select"
  | "site"
  | "tags"
  | "text"
  | "boolean"
  | "duration";

export interface FilterChipDef {
  id: string;
  label: string;
  priority: number;
  popoverType: FilterPopoverType;
  hasValue: (f: SearchFilters) => boolean;
  getDisplayValue: (f: SearchFilters, siteMap?: Map<string, string>) => string;
}

function truncate(s: string, max = 16): string {
  return s.length > max ? s.slice(0, max - 1) + "\u2026" : s;
}

function formatDateRange(from?: string, to?: string): string {
  if (from && to) return `${from.slice(0, 10)} \u2013 ${to.slice(0, 10)}`;
  if (from) return `From ${from.slice(0, 10)}`;
  if (to) return `To ${to.slice(0, 10)}`;
  return "All";
}

function formatDuration(min?: number, max?: number): string {
  if (min != null && max != null) return `${min}s \u2013 ${max}s`;
  if (min != null) return `\u2265 ${min}s`;
  if (max != null) return `\u2264 ${max}s`;
  return "All";
}

function boolLabel(v?: boolean): string {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "All";
}

export const FILTER_CHIP_DEFS: FilterChipDef[] = [
  // Priority 1 — always visible
  {
    id: "date",
    label: "Date",
    priority: 1,
    popoverType: "date",
    hasValue: (f) => !!(f.from || f.to),
    getDisplayValue: (f) => formatDateRange(f.from, f.to),
  },
  {
    id: "agent",
    label: "Agent",
    priority: 1,
    popoverType: "typeahead",
    hasValue: (f) => !!f.agentName,
    getDisplayValue: (f) => truncate(f.agentName || "All"),
  },
  {
    id: "type",
    label: "Type",
    priority: 1,
    popoverType: "select",
    hasValue: (f) => !!f.interactionType,
    getDisplayValue: (f) => f.interactionType || "All",
  },
  {
    id: "site",
    label: "Site",
    priority: 1,
    popoverType: "site",
    hasValue: (f) => !!f.siteId,
    getDisplayValue: (f, siteMap) => {
      if (!f.siteId) return "All";
      return truncate(siteMap?.get(f.siteId) || f.siteId);
    },
  },
  {
    id: "tags",
    label: "Tags",
    priority: 1,
    popoverType: "tags",
    hasValue: (f) => !!(f.tags && f.tags.length > 0),
    getDisplayValue: (f) => {
      if (!f.tags || f.tags.length === 0) return "All";
      if (f.tags.length === 1) return truncate(f.tags[0]);
      return `${f.tags.length} selected`;
    },
  },

  // Priority 2 — visible if space
  {
    id: "direction",
    label: "Direction",
    priority: 2,
    popoverType: "select",
    hasValue: (f) => !!f.direction,
    getDisplayValue: (f) => {
      if (f.direction === "in") return "Inbound";
      if (f.direction === "out") return "Outbound";
      return "All";
    },
  },
  {
    id: "phoneNumber",
    label: "Phone",
    priority: 2,
    popoverType: "typeahead",
    hasValue: (f) => !!f.phoneNumber,
    getDisplayValue: (f) => truncate(f.phoneNumber || "All"),
  },
  {
    id: "dnis",
    label: "DNIS",
    priority: 2,
    popoverType: "text",
    hasValue: (f) => !!f.dnis,
    getDisplayValue: (f) => truncate(f.dnis || "All"),
  },
  {
    id: "duration",
    label: "Duration",
    priority: 2,
    popoverType: "duration",
    hasValue: (f) => f.minDuration != null || f.maxDuration != null,
    getDisplayValue: (f) => formatDuration(f.minDuration, f.maxDuration),
  },

  // Priority 3 — overflow
  {
    id: "extension",
    label: "Extension",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.extension,
    getDisplayValue: (f) => truncate(f.extension || "All"),
  },
  {
    id: "device",
    label: "Device",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.device,
    getDisplayValue: (f) => truncate(f.device || "All"),
  },
  {
    id: "group",
    label: "Group",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.group,
    getDisplayValue: (f) => truncate(f.group || "All"),
  },
  {
    id: "skill",
    label: "Skill",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.skill,
    getDisplayValue: (f) => truncate(f.skill || "All"),
  },
  {
    id: "legalHold",
    label: "Legal Hold",
    priority: 3,
    popoverType: "boolean",
    hasValue: (f) => f.legalHold != null,
    getDisplayValue: (f) => boolLabel(f.legalHold),
  },
  {
    id: "hasMedia",
    label: "Has Media",
    priority: 3,
    popoverType: "boolean",
    hasValue: (f) => f.hasMedia != null,
    getDisplayValue: (f) => boolLabel(f.hasMedia),
  },
  {
    id: "sourceId",
    label: "Source ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.sourceId,
    getDisplayValue: (f) => truncate(f.sourceId || "All"),
  },
  {
    id: "interactionId",
    label: "Interaction ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.interactionId,
    getDisplayValue: (f) => truncate(f.interactionId || "All"),
  },
  {
    id: "masterInteractionId",
    label: "Master ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.masterInteractionId,
    getDisplayValue: (f) => truncate(f.masterInteractionId || "All"),
  },
  {
    id: "segmentId",
    label: "Segment ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.segmentId,
    getDisplayValue: (f) => truncate(f.segmentId || "All"),
  },
  {
    id: "callId",
    label: "Call ID",
    priority: 3,
    popoverType: "text",
    hasValue: (f) => !!f.callId,
    getDisplayValue: (f) => truncate(f.callId || "All"),
  },
  {
    id: "hasTranscription",
    label: "Has Transcript",
    priority: 3,
    popoverType: "boolean",
    hasValue: (f) => f.hasTranscription != null,
    getDisplayValue: (f) => boolLabel(f.hasTranscription),
  },
];
